import { createSlice, PayloadAction, Draft } from '@reduxjs/toolkit';
import { Reducer } from 'redux';
import { intersection } from 'lodash';
import {
  Field,
  FormView,
  getFormFilteredProducts,
  INITIAL_STATE,
  PersistedSelectedFilters,
} from './form-view';

const updateProductCodes = (state: Draft<FormView>) => {
  const derivedProductCodes = getFormFilteredProducts(state).map((p) => p.code);
  const productCodes = state.products.selectedOptions.map((p) => p.value);
  if (
    state.products.selectedOptions.length > 0 &&
    derivedProductCodes.length > 0
  ) {
    state.productCodes = intersection(derivedProductCodes, productCodes);
  } else if (state.products.selectedOptions.length > 0) {
    state.productCodes = productCodes;
  } else {
    state.productCodes = derivedProductCodes;
  }
};

const slice = createSlice({
  name: 'rfq-filters',
  initialState: INITIAL_STATE,
  reducers: {
    clearValues: (state) => {
      state.assets.selectedOptions = [];
      state.venues.selectedOptions = [];
      state.instruments.selectedOptions = [];
      state.auctionTypes.selectedOptions = [];
      state.products.selectedOptions = [];
      state.strategies.selectedOptions = [];
      state.counterparties.selectedOptions = [];
      state.notional = INITIAL_STATE.notional;

      state.productCodes = [];
    },
    clearField: (state, action: PayloadAction<Field>) => {
      state[action.payload].selectedOptions = [];
      updateProductCodes(state);
    },
    selectValues: function selectValues<T extends Field>(
      state: Draft<FormView>,
      action: PayloadAction<{
        field: T;
        values: FormView[T]['selectedOptions'];
      }>,
    ) {
      state[action.payload.field].selectedOptions = [...action.payload.values];
      updateProductCodes(state);
    },
    updateCounterparties: function updateCounterparties<T extends Field>(
      state: Draft<FormView>,
      action: PayloadAction<{
        field: T;
        options: FormView[T]['options'];
      }>,
    ) {
      state[action.payload.field].options = [...action.payload.options];
    },
    updateMinNotional: (state, action: PayloadAction<string>) => {
      state.notional.min = action.payload;
    },
    updateMaxNotional: (state, action: PayloadAction<string>) => {
      state.notional.max = action.payload;
    },
    hidrate: (
      state: Draft<FormView>,
      action: PayloadAction<PersistedSelectedFilters | null>,
    ) => {
      state.auctionTypes.selectedOptions = action.payload?.auctionTypes ?? [];
      state.instruments.selectedOptions = action.payload?.instruments ?? [];
      state.assets.selectedOptions = action.payload?.assets ?? [];
      state.venues.selectedOptions = action.payload?.venues ?? [];
      state.products.selectedOptions = action.payload?.products ?? [];
      state.strategies.selectedOptions = action.payload?.strategies ?? [];
      state.notional = action.payload?.notional ?? INITIAL_STATE.notional;
      state.counterparties.selectedOptions =
        action.payload?.counterparties ?? [];

      updateProductCodes(state);
    },
  },
});

export const { reducer } = slice as { reducer: Reducer<FormView> };
export const {
  clearValues,
  clearField,
  selectValues,
  hidrate,
  updateMinNotional,
  updateMaxNotional,
  updateCounterparties,
} = slice.actions;
